<template>
	<div class="cupping-score-card">
		<v-card>
			<v-card-title>
				<span class="text-h6">{{ score.total }}/{{ max }}</span>
				<iconify-icon @click="showScoreDetail" icon="mdi:information-outline"></iconify-icon>
			</v-card-title>
			<v-card-subtitle>
				{{ $formatTime(score.created_at) }}
				<span v-if="score.user_id && score.user">用户：{{ score.user.nickname }}</span>
				{{ score.notes }}
			</v-card-subtitle>
			<v-card-text>
				<score-radar :score="score" />
				<div v-if="score.aroma_notes && score.aroma_notes.length > 0">
					<v-chip class="text-h6 mr-1">香气备注</v-chip>
					{{ score.aroma_notes }}
				</div>
				<div v-if="score.flavor_notes && score.flavor_notes.length > 0">
					<v-chip class="text-h6 mr-1">风味备注</v-chip>
					{{ score.flavor_notes }}
				</div>
				<div v-if="score.aroma_tags && score.aroma_tags.length > 0">
					<v-chip class="text-h6 mr-1">香气标签</v-chip>
					{{ score.aroma_tags.join(',') }}
				</div>
				<div v-if="score.flavor_tags && score.flavor_tags.length > 0">
					<v-chip class="text-h6 mr-1">风味标签</v-chip>
					{{ score.flavor_tags.join(',') }}
				</div>
				<div v-if="score.primary_tastes && score.primary_tastes.length > 0">
					<v-chip class="text-h6 mr-1">口感备注</v-chip>
					{{ score.primary_tastes.join(',') }}
				</div>
			</v-card-text>
			<v-card-actions v-if="$slots.actions">
				<slot name="actions" v-bind="{ sample }"></slot>
			</v-card-actions>
		</v-card>
		<v-dialog v-model="showScoreDetailDlg" width="520" scrollable>
		<v-card>
			<v-card-title class="d-flex align-center">
				评分详情
				<v-spacer />
				<span class="text-h6">{{ displayTotal }}/{{ max }}</span>
			</v-card-title>
			<v-card-subtitle v-if="score">
				{{ $formatTime(score.created_at) }}
				<span v-if="score.user?.nickname">· {{ score.user.nickname }}</span>
			</v-card-subtitle>
			<v-card-text>
				<v-row dense>
					<v-col v-for="dim in detailDimensions" :key="dim.field" cols="6" sm="4">
						<div class="score-detail-item">
							<div class="text-caption text-medium-emphasis">{{ dim.name }}</div>
							<div class="text-h6">
								{{ formatScore(score[dim.field]) }}
								<span class="text-caption text-medium-emphasis">/{{ dim.max }}</span>
							</div>
							<v-progress-linear
								:model-value="scorePercent(score[dim.field], dim.max)"
								height="4"
								rounded
								color="primary"
								class="mt-1"
							/>
						</div>
					</v-col>
				</v-row>

				<v-divider class="my-3" />

				<div v-if="tagLines.length" class="mb-2">
					<div v-for="line in tagLines" :key="line.label" class="mb-2">
						<div class="text-caption text-medium-emphasis mb-1">{{ line.label }}</div>
						<div class="d-flex flex-wrap ga-1">
							<v-chip v-for="tag in line.tags" :key="`${line.label}-${tag}`" size="small" variant="tonal">
								{{ tag }}
							</v-chip>
						</div>
					</div>
				</div>

				<div v-if="noteLines.length" class="mb-2">
					<div v-for="line in noteLines" :key="line.label" class="mb-2">
						<div class="text-caption text-medium-emphasis">{{ line.label }}</div>
						<div class="text-body-2">{{ line.text }}</div>
					</div>
				</div>

				<div v-if="score.defects?.length" class="mb-2">
					<div class="text-caption text-medium-emphasis mb-1">
						瑕疵
						<span v-if="score.defects_total != null">（扣分 {{ score.defects_total }}）</span>
					</div>
					<div v-for="(d, i) in score.defects" :key="i" class="text-body-2">
						{{ d.name || '瑕疵' }}
						<span v-if="d.level" class="text-medium-emphasis">· {{ defectLevelLabel(d.level) }}</span>
						<span v-if="d.desc">— {{ d.desc }}</span>
					</div>
				</div>

				<div v-if="score.notes" class="mb-1">
					<div class="text-caption text-medium-emphasis">备注</div>
					<div class="text-body-2">{{ score.notes }}</div>
				</div>
			</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn variant="text" @click="showScoreDetailDlg = false">关闭</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
	</div>
</template>

<script>
import ScoreRadar from './ScoreRadar.vue'

export default {
	name: 'CuppingScoreCard',
	components: {
		ScoreRadar
	},
	props: {
		score: Object
	},
	data() {
		return {
			showScoreDetailDlg: false,
			dimensions: [
				{ name: '干香', field: 'aroma', max: 15 },
				{ name: '湿香', field: 'fragrance', max: 15 },
				{ name: '风味', field: 'flavor', max: 15 },
				{ name: '余韵', field: 'aftertaste', max: 15 },
				{ name: '酸质', field: 'acidity', max: 15 },
				{ name: '甜感', field: 'sweetness', max: 15 },
				{ name: '口感', field: 'body', max: 15 }
			]
		}
	},
	computed: {
		detailDimensions() {
			return [
				...this.dimensions
				// { name: '整体', field: 'overall', max: 15 }
			]
		},
		max() {
			return this.dimensions.reduce((acc, curr) => acc + curr.max, 0)
		},
		total() {
			const values = this.dimensions.map(({ field }) => Number(this.score?.[field]) || 0)
			return values.reduce((acc, curr) => acc + curr, 0)
		},
		displayTotal() {
			if (this.score?.total != null && this.score.total !== '') {
				return this.score.total
			}
			return Math.round(this.total * 10) / 10
		},
		tagLines() {
			const s = this.score || {}
			const lines = []
			const push = (label, tags) => {
				const list = Array.isArray(tags) ? tags.filter(Boolean) : []
				if (list.length) lines.push({ label, tags: list })
			}
			push('香气标签', s.aroma_tags)
			push('风味标签', s.flavor_tags)
			push('口感标签', s.body_tags)
			push('主要味道', s.primary_tastes)
			return lines
		},
		noteLines() {
			const s = this.score || {}
			const lines = []
			if (s.aroma_notes) lines.push({ label: '香气备注', text: s.aroma_notes })
			if (s.flavor_notes) lines.push({ label: '风味备注', text: s.flavor_notes })
			return lines
		}
	},
	methods: {
		showScoreDetail() {
			this.$emit('showScoreDetail', this.score)
			this.showScoreDetailDlg = true
		},
		formatScore(v) {
			if (v == null || v === '') return '—'
			const n = Number(v)
			if (Number.isNaN(n)) return String(v)
			return Number.isInteger(n) ? String(n) : n.toFixed(1)
		},
		scorePercent(v, max) {
			const n = Number(v) || 0
			const m = Number(max) || 1
			return Math.max(0, Math.min(100, (n / m) * 100))
		},
		defectLevelLabel(level) {
			const map = { quark: '轻瑕疵', full: '重瑕疵' }
			return map[level] || level
		},
		getRadarData() {
			const values = this.dimensions.map(({ field }) => this.score[field])
			return {
				textStyle: {
					fontWeight: 800
				},
				radar: {
					indicator: this.dimensions.map(({ name, max }) => ({ name, max }))
				},
				series: [
					{
						name: 'Value',
						type: 'radar',
						data: [
							{
								value: values
							}
						]
					}
				]
			}
		}
	}
}
</script>

<style scoped>
.cupping-score-card {
	width: 100%;
	max-width: 100%;
}
.cupping-score-card :deep(.v-card) {
	width: 100%;
}
.score-detail-item {
	padding: 0.25rem 0;
}
</style>
